import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isArray } from 'lodash';

import postTarget from 'Api/target/postTarget';
import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { targetUpsertCreateDomainText } from 'Const/target/text';
import { useTargetState } from 'View/target/state';

import getPostTargetRequest from './model/request/getPostTargetRequest';

const usePostTarget = () => {
  const { t } = useTranslation('target');
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { updateState, resetState, filterState, resetFilterState } = useTargetState();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = getPostTargetRequest(formData);
      return await postTarget({ payload });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['target', filterState] });

      onOpenToast({
        status: 'success',
        description: t('domain.successSaveNew'),
        duration: 5,
        alwaysShow: false,
      });

      resetState();
      resetFilterState();
    },
    onError: (error) => {
      const errorMessage = isArray(error?.response?.data?.errors) ? error?.response?.data?.errors[0]?.message : targetUpsertCreateDomainText?.errorDescription;

      onOpenToast({
        status: 'error',
        description: targetUpsertCreateDomainText?.errorDescription,
        duration: 5,
        alwaysShow: false,
      });

      updateState({
        isOpenFormUpsert: false,
        errorFields: {},
        alertProps: {
          isOpen: true,
          type: 'error',
          title: targetUpsertCreateDomainText?.errorTitle,
          description: errorMessage,
          onClose: () => updateState({ alertProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePostTarget;
